/**
 * Admin UI translations (简体中文 / English).
 *
 * Deliberately dependency-free: a dictionary, a `t()`, and a locale that
 * persists in `localStorage`. Switching the locale reloads the page, so module
 * state never has to flow through React.
 *
 * Keys are dotted and flat. A missing key falls back to English, then to the
 * key itself — so a forgotten translation is ugly, never fatal.
 */

export type Locale = "zh-CN" | "en-US";

const STORAGE_KEY = "admin-locale";

const en: Record<string, string> = {
  "app.title": "Blog CMS",
  "app.subtitle": "Admin",
  "nav.dashboard": "Dashboard",
  "nav.articles": "Articles",
  "nav.pages": "Pages",
  "nav.taxonomy": "Taxonomy",
  "nav.media": "Media",
  "nav.comments": "Comments",
  "nav.themes": "Themes",
  "nav.deployments": "Deployments",
  "nav.github": "GitHub",
  "nav.llm": "LLM Settings",
  "nav.language": "Language",
  "nav.signOut": "Sign out",

  "common.loading": "Loading…",
  "common.back": "Back",
  "common.never": "never",
  "common.configured": "configured",
  "common.notConfigured": "not configured",
  "common.status": "Status",
  "common.locale": "Locale",
  "common.title": "Title",
  "common.updated": "Updated",
  "common.path": "Path",
  "common.webhook": "Webhook",
  "common.installation": "Installation",
  "common.lastSync": "Last sync",
  "common.lastActivity": "Last activity",
  "common.untitled": "Untitled",
  "common.save": "Save",
  "common.cancel": "Cancel",

  "signin.title": "Sign in",
  "signin.body":
    "Under Cloudflare Access a reload signs you back in. In a deployment running the development-auth bypass, enter the admin password (rotatable on the Dashboard) or the break-glass secret.",
  "signin.placeholder": "Admin password or development secret",
  "signin.aria": "Admin password or development secret",
  "signin.button": "Sign in",

  "dashboard.title": "Dashboard",
  "dashboard.noSite": "No site is configured yet. Create one to get started.",
  "dashboard.published": "Published",
  "dashboard.drafts": "Drafts",
  "dashboard.scheduled": "Scheduled",
  "dashboard.entries": "Entries",
  "dashboard.entriesHint": "{posts} posts · {pages} pages",
  "dashboard.latestDeployment": "Latest deployment",
  "dashboard.deploymentDesc": "Build and publish status",
  "dashboard.noDeployments": "No deployments recorded yet.",
  "dashboard.commit": "Commit",
  "dashboard.when": "When",
  "dashboard.recentActivity": "Recent activity",
  "dashboard.nothingRecorded": "Nothing recorded yet.",
  "dashboard.lastChange": "Last change {time}",
  "dashboard.theme": "Theme:",
  "dashboard.locales": "Locales: {locales} · default {default}",
  "dashboard.passwordTitle": "Login password",
  "dashboard.passwordDesc":
    "Rotates the credential the sign-in prompt asks for. The deployment's break-glass secret keeps working.",
  "dashboard.currentPassword": "Current password",
  "dashboard.newPassword": "New password",
  "dashboard.confirmPassword": "Confirm new password",
  "dashboard.updatePassword": "Update password",
  "dashboard.updating": "Updating…",
  "dashboard.passwordChanged": "Password updated. Use the new password at the next sign-in.",
  "dashboard.passwordMismatch": "The two new passwords do not match.",
  "dashboard.passwordTooShort": "The new password must be at least 8 characters.",

  "articles.title": "Articles",
  "articles.countDesc": "{count} localizations",
  "articles.search": "Search title or slug…",
  "articles.allStatuses": "All statuses",
  "articles.allLocales": "All locales",
  "articles.sortUpdated": "Recently updated",
  "articles.sortPublished": "Recently published",
  "articles.sortTitle": "Title",
  "articles.sortStatus": "Status",
  "articles.newArticle": "New article",
  "articles.importWord": "Import Word",
  "articles.importAria": "Import a .docx document as a draft",
  "articles.importParsing": "Parsing document…",
  "articles.importUploading": "Transferring images…",
  "articles.importSaving": "Creating the draft…",
  "articles.importFailed": "Import failed",
  "articles.importNotDocx": "Only .docx files are supported.",
  "articles.importImagesFailed":
    "{count} image(s) could not be transferred and were left out of the draft.",
  "articles.noArticles": "No articles yet.",
  "articles.noMatch": "Nothing matches these filters.",
  "articles.emptyHint":
    "Create an article, or run a GitHub sync to import what is already published.",
  "articles.thTranslation": "Translation",
  "articles.addTranslationAria": "Add translation for {title}",
  "articles.thActions": "Actions",
  "articles.delete": "Delete",
  "articles.deleteAria": "Delete {title}",
  "articles.deleteConfirmDraft":
    "Delete \"{title}\" ({locale})? The draft records are removed. This cannot be undone.",
  "articles.deleteConfirmPublished":
    "\"{title}\" ({locale}) is published. Deleting it unpublishes it from the blog first, then removes the draft records; the file already committed to the content repository stays there. Continue?",
  "articles.deleteFailed": "Delete failed",
  "articles.addLocaleOption": "Add locale…",
  "articles.add": "Add",
  "articles.filterByStatus": "Filter by status",
  "articles.filterByLocale": "Filter by locale",
  "articles.sortBy": "Sort by",
  "articles.titleEdit": "Rename",
  "articles.titleEditAria": "Rename \"{title}\"",
  "articles.titleSaveFailed": "Rename failed",

  "editor.title": "Title",
  "editor.titlePlaceholder": "Title",
  "editor.bodyPlaceholder": "Start writing…",
  "editor.slug": "Slug",
  "editor.description": "Description",
  "editor.descriptionHint": "Shown in search results and social cards.",
  "editor.category": "Category",
  "editor.categoryHint": "One slug; pick an existing one or type a new one.",
  "editor.tags": "Tags",
  "editor.tagsHint": "Comma-separated lowercase slugs, e.g. cloudflare, astro",
  "editor.cover": "Cover image",
  "editor.coverHint": "Shown in lists and social cards. Paste an uploaded image URL.",
  "editor.loadingDraft": "Loading the draft…",
  "editor.saveDraft": "Save draft",
  "editor.preview": "Preview",
  "editor.unpublish": "Unpublish",
  "editor.publish": "Publish",
  "editor.saved": "Draft saved.",
  "editor.publishedAs": "Published as {sha}.",
  "editor.publishedOver": "Published over the external change as {sha}.",
  "editor.unpublished": "Unpublished. The page is no longer built.",
  "editor.publishAnyway": "Publish my version anyway",
  "editor.previewHeading": "Preview",
  "editor.autosaving": "Saving…",
  "editor.autosaved": "Saved {time}",
  "editor.autosaveFailed": "Save failed — click to retry",
  "editor.uploading": "Uploading {count}…",
  "editor.uploadFailed": "Upload failed",
  "editor.uploadImage": "Upload image",
  "editor.uploadAttachment": "Upload attachment",
  "editor.importWord": "Import Word",
  "editor.importConfirm":
    "Importing replaces this article's title and content (slug, locale and metadata stay). Continue?",
  "editor.importFailed": "Import failed",
  "editor.settings": "Settings",
  "editor.settingsTitle": "Post settings",
  "editor.aiGenerate": "Generate with AI",
  "editor.aiSuggest": "Suggest with AI",
  "editor.aiNeedsTitle": "Write a title first — the AI names the article from it.",
  "editor.aiCategoryNew": "No existing category fits. Consider creating \"{name}\".",
  "editor.aiFailed": "AI request failed",
  "editor.tbUndo": "Undo",
  "editor.tbRedo": "Redo",
  "editor.tbParagraph": "Text",
  "editor.tbHeading1": "Heading 1",
  "editor.tbHeading2": "Heading 2",
  "editor.tbHeading3": "Heading 3",
  "editor.tbHeading4": "Heading 4",
  "editor.tbBold": "Bold",
  "editor.tbItalic": "Italic",
  "editor.tbUnderline": "Underline",
  "editor.tbStrike": "Strikethrough",
  "editor.tbCode": "Inline code",
  "editor.tbClearFormat": "Clear formatting",
  "editor.tbBulletList": "Bulleted list",
  "editor.tbOrderedList": "Numbered list",
  "editor.tbTaskList": "Task list",
  "editor.tbBlockquote": "Quote",
  "editor.tbCodeBlock": "Code block",
  "editor.tbHr": "Horizontal rule",
  "editor.tbLink": "Link",
  "editor.tbLinkPrompt": "Link URL",
  "editor.tbTable": "Insert table",
  "editor.tbRow": "Insert row",
  "editor.tbRowDelete": "Delete row",
  "editor.tbColumn": "Insert column",
  "editor.tbColumnDelete": "Delete column",
  "editor.tbHeaderRow": "Toggle header row",
  "editor.tbTableDelete": "Delete table",
  "editor.info": "Info",
  "editor.infoTitle": "Post info",
  "editor.infoCharacters": "Characters",
  "editor.infoPath": "Content path",
  "editor.closePreview": "Close preview",
  "editor.contentPath": "Content path",
  "editor.contentPathHint": "Repository directory, e.g. posts/my-first-post",
  "editor.canonicalLocale": "Canonical locale",

  "pages.title": "Pages",
  "pages.noPages": "No pages yet.",
  "pages.emptyHint": "Create a page, or run a GitHub sync to import what is already published.",

  "taxonomy.desc":
    "The vocabulary articles draw from. Entries appear here when published or synced; names are yours to edit.",
  "taxonomy.categoriesTitle": "Categories",
  "taxonomy.tagsTitle": "Tags",
  "taxonomy.slug": "Slug",
  "taxonomy.name": "Display name",
  "taxonomy.used": "Used",
  "taxonomy.termCount": "{count} terms",
  "taxonomy.add": "Add",
  "taxonomy.save": "Save",
  "taxonomy.delete": "Delete",
  "taxonomy.newSlug": "new-slug",
  "taxonomy.newName": "Display name (optional)",
  "taxonomy.empty": "Nothing here yet — publish an article with a category or tag, or add one above.",
  "taxonomy.saved": "Saved.",
  "taxonomy.deleted": "Deleted.",

  "media.title": "Media",
  "media.countDesc": "{count} files",
  "media.search": "Search filenames…",
  "media.noMedia": "No media yet.",
  "media.noMatch": "Nothing matches this search.",
  "media.emptyHint": "Images and attachments are uploaded from the article editor.",
  "media.thPreview": "Preview",
  "media.thFilename": "Filename",
  "media.thType": "Type",
  "media.thSize": "Size",
  "media.thUploaded": "Uploaded",
  "media.copyUrl": "Copy URL",
  "media.copied": "Copied",
  "media.delete": "Delete",

  "comments.title": "Comments",
  "comments.countDesc": "{count} mapped threads",
  "comments.noDiscussions": "No discussions yet.",
  "comments.emptyHint": "A discussion is created when a localization is published.",
  "comments.thProvider": "Provider",
  "comments.thDiscussionNumber": "Discussion #",
  "comments.thMapped": "Mapped",

  "deployments.title": "Deployments",
  "deployments.countDesc": "{count} recent builds",
  "deployments.none": "No deployments recorded.",
  "deployments.emptyHint": "A build reports itself here after it runs.",
  "deployments.thCommit": "Commit",
  "deployments.thStarted": "Started",
  "deployments.thUrl": "URL",

  "themes.title": "Themes",
  "themes.desc":
    "The theme decides how the public site renders. Switching a theme never changes your content.",
  "themes.installed": "Installed themes",
  "themes.current": "Current selection: {selection}",
  "themes.active": "active",
  "themes.save": "Save theme",
  "themes.saving": "Saving…",
  "themes.setNoCommit": "Theme set to {theme}.",
  "themes.setCommit": "Theme set to {theme} (config commit {sha}). The blog rebuilds automatically.",
  "themes.loadFailed": "Failed to load theme settings",
  "themes.noSite": "No site is available.",

  "github.title": "GitHub",
  "github.desc": "Connection, webhook and index status",
  "github.syncNow": "Sync now",
  "github.synced":
    "Synced {rev}: {created} created, {updated} updated, {localizations} localizations.",
  "github.syncFailed": "Sync failed.",
  "github.connection": "Connection",
  "github.index": "Index",
  "github.indexDesc": "What D1 currently holds. It can always be rebuilt from the repository.",
  "github.thKind": "Kind",
  "github.thState": "State",
  "github.thCount": "Count",
  "github.kindEntry": "Entry",
  "github.kindLocalization": "Localization",
  "github.signinPrompt": "Sign in",

  "llm.title": "LLM Settings",
  "llm.desc":
    "Connect OpenAI-compatible model endpoints for AI features. Keys are stored server-side and never shown again.",
  "llm.configured": "Configured providers",
  "llm.countDesc": "{count} providers",
  "llm.empty": "No providers configured yet. Add one below.",
  "llm.addTitle": "Add a provider",
  "llm.editTitle": "Edit \"{name}\"",
  "llm.preset": "Provider preset",
  "llm.presetHint": "Picking a preset fills the form — every field stays editable.",
  "llm.presetCustom": "Custom (OpenAI-compatible)",
  "llm.name": "Name",
  "llm.namePlaceholder": "e.g. OpenAI main",
  "llm.baseUrl": "Base URL",
  "llm.baseUrlHint": "OpenAI-compatible root, e.g. https://api.openai.com/v1",
  "llm.apiKey": "API key",
  "llm.apiKeyCreateHint": "Leave empty if the endpoint needs no key (e.g. local Ollama).",
  "llm.apiKeyEditHint": "Leave empty to keep the stored key.",
  "llm.clearKey": "Clear stored key",
  "llm.keyNone": "no key",
  "llm.apiVersion": "API version",
  "llm.apiVersionHint": "Azure-style api-version query, when the provider needs one.",
  "llm.defaultModel": "Default model",
  "llm.enabled": "Enabled",
  "llm.disabled": "Disabled",
  "llm.save": "Save provider",
  "llm.saving": "Saving…",
  "llm.cancel": "Cancel",
  "llm.saved": "Saved.",
  "llm.edit": "Edit",
  "llm.delete": "Delete",
  "llm.deleteConfirm": "Delete provider \"{name}\"?",
  "llm.verify": "Test connection",
  "llm.verifying": "Testing…",
  "llm.verifyOk": "Connected — {count} models available.",
  "llm.verifyOkNoModels": "Connected, but the endpoint returned no model list.",
  "llm.verifyFail": "Connection failed: {error}",
  "llm.loadFailed": "Failed to load LLM settings",
  "llm.noSite": "No site is available.",
  "llm.modelLoading": "Loading models…",
  "llm.modelLoadFailed": "Could not load models: {error}",
  "llm.modelRetry": "Retry",
  "llm.modelNoMatch": "No model matches \"{query}\" — the value you typed is kept.",
  "llm.modelEndpointEmpty": "The endpoint returned no models.",
  "llm.modelMore": "{count} more — keep typing to narrow the list.",
  "llm.modelSaveHint": "The live model list loads from the endpoint once the provider is saved.",
  "llm.defaultBadge": "Default",
  "llm.setDefault": "Set as default",
  "llm.defaultHint":
    "AI assistance (URL, tags, category suggestions) uses the provider marked as default and its configured model.",
};

const zh: Record<string, string> = {
  "app.title": "博客 CMS",
  "app.subtitle": "管理端",
  "nav.dashboard": "仪表盘",
  "nav.articles": "文章",
  "nav.pages": "页面",
  "nav.taxonomy": "分类与标签",
  "nav.media": "媒体",
  "nav.comments": "评论",
  "nav.themes": "主题",
  "nav.deployments": "部署",
  "nav.github": "GitHub",
  "nav.llm": "LLM 设置",
  "nav.language": "语言",
  "nav.signOut": "注销",

  "common.loading": "加载中…",
  "common.back": "返回",
  "common.never": "从未",
  "common.configured": "已配置",
  "common.notConfigured": "未配置",
  "common.status": "状态",
  "common.locale": "语言",
  "common.title": "标题",
  "common.updated": "更新时间",
  "common.path": "路径",
  "common.webhook": "Webhook",
  "common.installation": "安装",
  "common.lastSync": "最近同步",
  "common.lastActivity": "最近活动",
  "common.untitled": "未命名",
  "common.save": "保存",
  "common.cancel": "取消",

  "signin.title": "登录",
  "signin.body":
    "使用 Cloudflare Access 时,刷新页面即可自动登录。使用开发鉴权模式的部署,请输入管理密码(可在仪表盘轮换)或应急密钥。",
  "signin.placeholder": "管理密码或开发密钥",
  "signin.aria": "管理密码或开发密钥",
  "signin.button": "登录",

  "dashboard.title": "仪表盘",
  "dashboard.noSite": "还没有配置站点。先创建一个再开始。",
  "dashboard.published": "已发布",
  "dashboard.drafts": "草稿",
  "dashboard.scheduled": "定时",
  "dashboard.entries": "条目",
  "dashboard.entriesHint": "{posts} 篇文章 · {pages} 个页面",
  "dashboard.latestDeployment": "最近部署",
  "dashboard.deploymentDesc": "构建与发布状态",
  "dashboard.noDeployments": "还没有部署记录。",
  "dashboard.commit": "提交",
  "dashboard.when": "时间",
  "dashboard.recentActivity": "最近活动",
  "dashboard.nothingRecorded": "暂无记录。",
  "dashboard.lastChange": "最近变更 {time}",
  "dashboard.theme": "主题:",
  "dashboard.locales": "语言:{locales} · 默认 {default}",
  "dashboard.passwordTitle": "登录密码",
  "dashboard.passwordDesc": "轮换登录页使用的凭据。部署时的应急密钥仍然有效。",
  "dashboard.currentPassword": "当前密码",
  "dashboard.newPassword": "新密码",
  "dashboard.confirmPassword": "确认新密码",
  "dashboard.updatePassword": "更新密码",
  "dashboard.updating": "更新中…",
  "dashboard.passwordChanged": "密码已更新,下次登录请使用新密码。",
  "dashboard.passwordMismatch": "两次输入的新密码不一致。",
  "dashboard.passwordTooShort": "新密码至少需要 8 个字符。",

  "articles.title": "文章",
  "articles.countDesc": "{count} 个语言版本",
  "articles.search": "搜索标题或 slug…",
  "articles.allStatuses": "全部状态",
  "articles.allLocales": "全部语言",
  "articles.sortUpdated": "最近更新",
  "articles.sortPublished": "最近发布",
  "articles.sortTitle": "标题",
  "articles.sortStatus": "状态",
  "articles.newArticle": "新建文章",
  "articles.importWord": "导入 Word",
  "articles.importAria": "将 .docx 文档导入为草稿",
  "articles.importParsing": "解析文档中…",
  "articles.importUploading": "转存图片中…",
  "articles.importSaving": "创建草稿…",
  "articles.importFailed": "导入失败",
  "articles.importNotDocx": "仅支持 .docx 文件。",
  "articles.importImagesFailed": "{count} 张图片转存失败,未能进入草稿。",
  "articles.noArticles": "还没有文章。",
  "articles.noMatch": "没有符合筛选条件的文章。",
  "articles.emptyHint": "创建一篇文章,或运行 GitHub 同步导入已发布的内容。",
  "articles.thTranslation": "翻译",
  "articles.addTranslationAria": "为「{title}」添加翻译",
  "articles.thActions": "操作",
  "articles.delete": "删除",
  "articles.deleteAria": "删除「{title}」",
  "articles.deleteConfirmDraft": "确定删除「{title}」({locale} 版)吗?草稿记录会被移除,此操作不可恢复。",
  "articles.deleteConfirmPublished":
    "「{title}」({locale} 版)已发布。删除会先把它从博客下线,再移除草稿记录;已提交到内容仓库的文件会保留。确定继续吗?",
  "articles.deleteFailed": "删除失败",
  "articles.addLocaleOption": "添加语言…",
  "articles.add": "添加",
  "articles.filterByStatus": "按状态筛选",
  "articles.filterByLocale": "按语言筛选",
  "articles.sortBy": "排序",
  "articles.titleEdit": "重命名",
  "articles.titleEditAria": "重命名「{title}」",
  "articles.titleSaveFailed": "重命名失败",

  "editor.title": "标题",
  "editor.titlePlaceholder": "标题",
  "editor.bodyPlaceholder": "开始写作…",
  "editor.slug": "Slug",
  "editor.description": "描述",
  "editor.descriptionHint": "显示在搜索结果和社交分享卡片中。",
  "editor.category": "分类",
  "editor.categoryHint": "一个 slug;可选择已有分类或输入新的。",
  "editor.tags": "标签",
  "editor.tagsHint": "小写 slug,用逗号分隔,例如 cloudflare, astro",
  "editor.cover": "封面图片",
  "editor.coverHint": "显示在列表和社交分享卡片中。可粘贴已上传图片的链接。",
  "editor.loadingDraft": "草稿加载中…",
  "editor.saveDraft": "保存草稿",
  "editor.preview": "预览",
  "editor.unpublish": "下线",
  "editor.publish": "发布",
  "editor.saved": "草稿已保存。",
  "editor.publishedAs": "已发布,提交 {sha}。",
  "editor.publishedOver": "已覆盖外部变更发布,提交 {sha}。",
  "editor.unpublished": "已下线,该页面不再构建。",
  "editor.publishAnyway": "仍要发布我的版本",
  "editor.previewHeading": "预览",
  "editor.autosaving": "保存中…",
  "editor.autosaved": "已保存 {time}",
  "editor.autosaveFailed": "保存失败,点击重试",
  "editor.uploading": "正在上传 {count} 个文件…",
  "editor.uploadFailed": "上传失败",
  "editor.uploadImage": "上传图片",
  "editor.uploadAttachment": "上传附件",
  "editor.importWord": "导入 Word",
  "editor.importConfirm": "导入会覆盖当前文章的标题和内容(slug、语言和分类等设置保持不变),确定继续吗?",
  "editor.importFailed": "导入失败",
  "editor.settings": "设置",
  "editor.settingsTitle": "文章设置",
  "editor.aiGenerate": "AI 生成",
  "editor.aiSuggest": "AI 推荐",
  "editor.aiNeedsTitle": "请先填写标题,AI 需要根据标题来生成。",
  "editor.aiCategoryNew": "现有分类没有合适的,推荐新建:「{name}」。",
  "editor.aiFailed": "AI 调用失败",
  "editor.tbUndo": "撤销",
  "editor.tbRedo": "重做",
  "editor.tbParagraph": "正文",
  "editor.tbHeading1": "标题 1",
  "editor.tbHeading2": "标题 2",
  "editor.tbHeading3": "标题 3",
  "editor.tbHeading4": "标题 4",
  "editor.tbBold": "加粗",
  "editor.tbItalic": "斜体",
  "editor.tbUnderline": "下划线",
  "editor.tbStrike": "删除线",
  "editor.tbCode": "行内代码",
  "editor.tbClearFormat": "清除格式",
  "editor.tbBulletList": "无序列表",
  "editor.tbOrderedList": "有序列表",
  "editor.tbTaskList": "任务列表",
  "editor.tbBlockquote": "引用",
  "editor.tbCodeBlock": "代码块",
  "editor.tbHr": "分割线",
  "editor.tbLink": "链接",
  "editor.tbLinkPrompt": "输入链接地址",
  "editor.tbTable": "插入表格",
  "editor.tbRow": "插入行",
  "editor.tbRowDelete": "删除行",
  "editor.tbColumn": "插入列",
  "editor.tbColumnDelete": "删除列",
  "editor.tbHeaderRow": "切换表头行",
  "editor.tbTableDelete": "删除表格",
  "editor.info": "信息",
  "editor.infoTitle": "文章信息",
  "editor.infoCharacters": "字符数",
  "editor.infoPath": "内容路径",
  "editor.closePreview": "关闭预览",
  "editor.contentPath": "内容路径",
  "editor.contentPathHint": "仓库中的目录,例如 posts/my-first-post",
  "editor.canonicalLocale": "主语言",

  "pages.title": "页面",
  "pages.noPages": "还没有页面。",
  "pages.emptyHint": "创建一个页面,或运行 GitHub 同步导入已发布的内容。",

  "taxonomy.desc": "文章使用的词表。发布或同步后词条会自动出现在这里;显示名称可以随时修改。",
  "taxonomy.categoriesTitle": "分类",
  "taxonomy.tagsTitle": "标签",
  "taxonomy.slug": "标识(slug)",
  "taxonomy.name": "显示名称",
  "taxonomy.used": "引用",
  "taxonomy.termCount": "{count} 个词条",
  "taxonomy.add": "添加",
  "taxonomy.save": "保存",
  "taxonomy.delete": "删除",
  "taxonomy.newSlug": "new-slug",
  "taxonomy.newName": "显示名称(可选)",
  "taxonomy.empty": "这里还是空的——发布一篇带分类或标签的文章,或在上方添加。",
  "taxonomy.saved": "已保存。",
  "taxonomy.deleted": "已删除。",

  "media.title": "媒体",
  "media.countDesc": "{count} 个文件",
  "media.search": "搜索文件名…",
  "media.noMedia": "还没有媒体文件。",
  "media.noMatch": "没有符合搜索条件的文件。",
  "media.emptyHint": "图片和附件从文章编辑器上传。",
  "media.thPreview": "预览",
  "media.thFilename": "文件名",
  "media.thType": "类型",
  "media.thSize": "大小",
  "media.thUploaded": "上传时间",
  "media.copyUrl": "复制链接",
  "media.copied": "已复制",
  "media.delete": "删除",

  "comments.title": "评论",
  "comments.countDesc": "{count} 个已映射的讨论",
  "comments.noDiscussions": "还没有讨论。",
  "comments.emptyHint": "发布某个语言版本时会自动创建对应讨论。",
  "comments.thProvider": "提供方",
  "comments.thDiscussionNumber": "讨论编号",
  "comments.thMapped": "映射时间",

  "deployments.title": "部署",
  "deployments.countDesc": "最近 {count} 次构建",
  "deployments.none": "还没有部署记录。",
  "deployments.emptyHint": "构建完成后会自动在此上报。",
  "deployments.thCommit": "提交",
  "deployments.thStarted": "开始时间",
  "deployments.thUrl": "链接",

  "themes.title": "主题",
  "themes.desc": "主题决定公开网站的呈现方式。切换主题永远不会改动你的内容。",
  "themes.installed": "已安装主题",
  "themes.current": "当前选择:{selection}",
  "themes.active": "使用中",
  "themes.save": "保存主题",
  "themes.saving": "保存中…",
  "themes.setNoCommit": "主题已设为 {theme}。",
  "themes.setCommit": "主题已设为 {theme}(配置提交 {sha})。博客将自动重新构建。",
  "themes.loadFailed": "主题设置加载失败",
  "themes.noSite": "当前没有可用站点。",

  "github.title": "GitHub",
  "github.desc": "连接、Webhook 与索引状态",
  "github.syncNow": "立即同步",
  "github.synced": "已同步 {rev}:新建 {created},更新 {updated},语言版本 {localizations}。",
  "github.syncFailed": "同步失败。",
  "github.connection": "连接",
  "github.index": "索引",
  "github.indexDesc": "D1 当前的内容。随时可以从内容仓库重建。",
  "github.thKind": "类型",
  "github.thState": "状态",
  "github.thCount": "数量",
  "github.kindEntry": "条目",
  "github.kindLocalization": "语言版本",
  "github.signinPrompt": "登录",

  "llm.title": "LLM 设置",
  "llm.desc": "连接 OpenAI 兼容的大模型服务,供 AI 功能使用。密钥保存在服务端,保存后不再显示。",
  "llm.configured": "已配置的服务",
  "llm.countDesc": "{count} 个服务",
  "llm.empty": "还没有配置,在下方添加一个。",
  "llm.addTitle": "添加服务",
  "llm.editTitle": "编辑「{name}」",
  "llm.preset": "服务商预设",
  "llm.presetHint": "选择预设会自动填充表单,所有字段仍可修改。",
  "llm.presetCustom": "自定义(OpenAI 兼容)",
  "llm.name": "名称",
  "llm.namePlaceholder": "例如:OpenAI 主力",
  "llm.baseUrl": "接口地址 (Base URL)",
  "llm.baseUrlHint": "OpenAI 兼容的根地址,例如 https://api.openai.com/v1",
  "llm.apiKey": "API 密钥",
  "llm.apiKeyCreateHint": "本地服务(如 Ollama)无需密钥时可留空。",
  "llm.apiKeyEditHint": "留空表示保持已存密钥不变。",
  "llm.clearKey": "清除已存密钥",
  "llm.keyNone": "未设置密钥",
  "llm.apiVersion": "API 版本",
  "llm.apiVersionHint": "Azure 风格的 api-version 参数,仅部分服务商需要。",
  "llm.defaultModel": "默认模型",
  "llm.enabled": "启用",
  "llm.disabled": "已停用",
  "llm.save": "保存",
  "llm.saving": "保存中…",
  "llm.cancel": "取消",
  "llm.saved": "已保存。",
  "llm.edit": "编辑",
  "llm.delete": "删除",
  "llm.deleteConfirm": "确定删除「{name}」?",
  "llm.verify": "测试连接",
  "llm.verifying": "测试中…",
  "llm.verifyOk": "连接成功,可用模型 {count} 个。",
  "llm.verifyOkNoModels": "连接成功,但服务未返回模型列表。",
  "llm.verifyFail": "连接失败:{error}",
  "llm.loadFailed": "LLM 设置加载失败",
  "llm.noSite": "当前没有可用站点。",
  "llm.modelLoading": "正在获取模型列表…",
  "llm.modelLoadFailed": "获取模型列表失败:{error}",
  "llm.modelRetry": "重试",
  "llm.modelNoMatch": "没有匹配「{query}」的模型,将保留你输入的值。",
  "llm.modelEndpointEmpty": "接口没有返回模型。",
  "llm.modelMore": "还有 {count} 个,继续输入可缩小范围。",
  "llm.modelSaveHint": "保存服务后,模型列表会从接口自动获取。",
  "llm.defaultBadge": "默认",
  "llm.setDefault": "设为默认",
  "llm.defaultHint": "AI 功能(生成 URL、标签、分类推荐)使用标记为「默认」的服务及其配置的模型。",
};

const dictionaries: Record<Locale, Record<string, string>> = {
  "en-US": en,
  "zh-CN": zh,
};

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored === "zh-CN" || stored === "en-US") {
      return stored;
    }

    return navigator.language.toLowerCase().startsWith("zh") ? "zh-CN" : "en-US";
  } catch {
    return "en-US";
  }
}

let current: Locale = initialLocale();

/** The active locale. */
export function getLocale(): Locale {
  return current;
}

/** Persist a locale and reload, so every module picks it up. */
export function setLocale(next: Locale): void {
  current = next;
  localStorage.setItem(STORAGE_KEY, next);
  window.location.reload();
}

/** All locales with their display names, for the switcher. */
export function locales(): readonly { readonly value: Locale; readonly label: string }[] {
  return [
    { value: "zh-CN", label: "简体中文" },
    { value: "en-US", label: "English" },
  ];
}

/**
 * Translate `key`, interpolating `{name}` parameters.
 *
 * Falls back to English, then to the key itself.
 */
export function t(key: string, params?: Record<string, string | number>): string {
  const template = dictionaries[current][key] ?? en[key] ?? key;

  if (params === undefined) {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}
